import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Search, Users, Star, DollarSign, ShoppingBag, Edit, Phone, Mail, Wallet, Shield } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import api from '../lib/api';

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSales, setCustomerSales] = useState([]);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', address: '', notes: '', credit_limit: '', loyalty_enabled: false });
  const [redeemAmount, setRedeemAmount] = useState('');
  const [orgSettings, setOrgSettings] = useState(null);

  const formatCurrency = (amount) => `J$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;

  const fetchCustomers = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await api.get(`/customers${params}`);
      setCustomers(res.data);
    } catch (e) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchCustomers();
    api.get('/organization').then(r => setOrgSettings(r.data)).catch(() => {});
  }, [search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, credit_limit: formData.credit_limit === '' ? 0 : Number(formData.credit_limit) || 0 };
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, payload);
        toast.success('Customer updated');
      } else {
        await api.post('/customers', payload);
        toast.success('Customer added');
      }
      setShowForm(false);
      setEditingCustomer(null);
      setFormData({ name: '', phone: '', email: '', address: '', notes: '', credit_limit: '', loyalty_enabled: false });
      fetchCustomers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save customer');
      toast.error(msg);
    }
  };

  const handleEdit = (c) => {
    setEditingCustomer(c);
    setFormData({ name: c.name, phone: c.phone || '', email: c.email || '', address: c.address || '', notes: c.notes || '', credit_limit: c.credit_limit || '', loyalty_enabled: c.loyalty_enabled || false });
    setShowForm(true);
  };

  const handleViewCustomer = async (c) => {
    setSelectedCustomer(c);
    setRedeemAmount('');
    try {
      const res = await api.get(`/customers/${c.id}/sales`);
      setCustomerSales(res.data);
    } catch (e) {
      setCustomerSales([]);
    }
  };

  const handleRedeemPoints = async () => {
    const points = parseInt(redeemAmount);
    if (!points || points <= 0) return;
    try {
      const res = await api.post(`/customers/${selectedCustomer.id}/redeem`, { points });
      toast.success(res.data.message);
      setSelectedCustomer({ ...selectedCustomer, points: res.data.remaining_points });
      setRedeemAmount('');
      fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to redeem points');
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Customers</h1>
          <p className="text-sm text-[#0A0A0A]/50">Manage customers and loyalty rewards</p>
        </div>
        <Button onClick={() => { setEditingCustomer(null); setFormData({ name: '', phone: '', email: '', address: '', notes: '', credit_limit: '', loyalty_enabled: false }); setShowForm(true); }} className="bg-[#007AFF]" data-testid="add-customer-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#007AFF]" />
            </div>
            <div>
              <p className="text-2xl font-bold">{customers.length}</p>
              <p className="text-xs text-[#0A0A0A]/50">Total Customers</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#34C759]/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#34C759]" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatCurrency(customers.reduce((sum, c) => sum + (c.total_spent || 0), 0))}</p>
              <p className="text-xs text-[#0A0A0A]/50">Total Revenue</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FFD700]/10 flex items-center justify-center">
              <Star className="w-5 h-5 text-[#FFD700]" />
            </div>
            <div>
              <p className="text-2xl font-bold">{customers.reduce((sum, c) => sum + (c.points || 0), 0)}</p>
              <p className="text-xs text-[#0A0A0A]/50">Total Points</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF9500]/10 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-[#FF9500]" />
            </div>
            <div>
              <p className="text-2xl font-bold">{customers.reduce((sum, c) => sum + (c.visit_count || 0), 0)}</p>
              <p className="text-xs text-[#0A0A0A]/50">Total Visits</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A0A0A]/40" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, phone, or account #..."
          className="pl-10"
          data-testid="customer-search"
        />
      </div>

      {/* Customer List */}
      <div className="grid gap-3">
        {loading ? (
          <p className="text-center py-8 text-[#0A0A0A]/50">Loading...</p>
        ) : customers.length === 0 ? (
          <Card className="border border-[#E5E5EA] shadow-none">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-[#0A0A0A]/20 mx-auto mb-3" />
              <p className="text-[#0A0A0A]/50">No customers yet. Add your first customer!</p>
            </CardContent>
          </Card>
        ) : customers.map(c => (
          <Card key={c.id} className="border border-[#E5E5EA] shadow-none hover:border-[#007AFF]/30 transition-colors cursor-pointer" onClick={() => handleViewCustomer(c)} data-testid={`customer-${c.account_number || c.code}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center text-[#007AFF] font-bold text-sm">
                  {c.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                    {c.name}
                    {orgSettings?.loyalty_feature_enabled && c.loyalty_enabled && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FFD700]/15 text-[#D4A800]"><Star className="w-2.5 h-2.5" />LOYALTY</span>}
                  </div>
                  <div className="text-xs text-[#0A0A0A]/50 flex items-center gap-3">
                    <span className="font-mono">{c.account_number || c.code}</span>
                    {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right hidden sm:block">
                  <div className="text-sm font-semibold">{formatCurrency(c.total_spent)}</div>
                  <div className="text-xs text-[#0A0A0A]/40">spent</div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="text-sm font-semibold text-[#FFD700]">{c.points || 0} pts</div>
                  <div className="text-xs text-[#0A0A0A]/40">{c.visit_count || 0} visits</div>
                </div>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(c); }} data-testid={`edit-customer-${c.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Customer name" data-testid="customer-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="876-000-0000" data-testid="customer-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" data-testid="customer-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Customer address" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Preferred items, special requests, etc." />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Wallet className="w-3.5 h-3.5 text-[#FF9500]" /> Credit Limit (J$)</Label>
              <Input type="number" value={formData.credit_limit} onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value === '' ? '' : Number(e.target.value) })} placeholder="0 = no credit" min="0" step="100" data-testid="customer-credit-limit-input" />
              <p className="text-xs text-[#0A0A0A]/40">Set a credit limit to allow this customer to buy on credit</p>
            </div>
            {orgSettings?.loyalty_feature_enabled && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-[#E5E5EA]">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#FFD700]" />
                  <div>
                    <Label className="cursor-pointer">Loyalty Program</Label>
                    <p className="text-xs text-[#0A0A0A]/40">Enable points earning & redemption</p>
                  </div>
                </div>
                <Switch 
                  checked={formData.loyalty_enabled} 
                  onCheckedChange={(checked) => setFormData({ ...formData, loyalty_enabled: checked })}
                  data-testid="customer-loyalty-toggle"
                />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#007AFF]" data-testid="save-customer-btn">{editingCustomer ? 'Update' : 'Add Customer'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="max-w-lg">
          {selectedCustomer && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center text-[#007AFF] font-bold">
                    {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div>{selectedCustomer.name}</div>
                    <div className="text-xs font-normal text-[#0A0A0A]/50 font-mono">{selectedCustomer.account_number || selectedCustomer.code}</div>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-sm text-[#0A0A0A]/60">
                  {selectedCustomer.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{selectedCustomer.phone}</span>}
                  {selectedCustomer.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{selectedCustomer.email}</span>}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[#34C759]/10 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-[#34C759]">{formatCurrency(selectedCustomer.total_spent)}</div>
                    <div className="text-xs text-[#0A0A0A]/50">Total Spent</div>
                  </div>
                  <div className="bg-[#FFD700]/10 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-[#D4A800]">{selectedCustomer.points || 0}</div>
                    <div className="text-xs text-[#0A0A0A]/50">Points</div>
                  </div>
                  <div className="bg-[#007AFF]/10 rounded-lg p-3 text-center">
                    <div className="text-lg font-bold text-[#007AFF]">{selectedCustomer.visit_count || 0}</div>
                    <div className="text-xs text-[#0A0A0A]/50">Visits</div>
                  </div>
                </div>
                {(selectedCustomer.credit_limit > 0 || selectedCustomer.credit_balance > 0) && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Wallet className="w-4 h-4 text-[#FF9500]" /> Credit Account</h4>
                    <div className="bg-[#FF9500]/5 border border-[#FF9500]/20 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#0A0A0A]/60">Credit Limit:</span>
                        <span className="font-mono font-semibold">{formatCurrency(selectedCustomer.credit_limit)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#0A0A0A]/60">Outstanding:</span>
                        <span className="font-mono font-semibold text-[#FF3B30]">{formatCurrency(selectedCustomer.credit_balance || 0)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#0A0A0A]/60">Available:</span>
                        <span className="font-mono font-semibold text-[#34C759]">{formatCurrency((selectedCustomer.credit_limit || 0) - (selectedCustomer.credit_balance || 0))}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <h4 className="font-semibold text-sm mb-2">Loyalty Points</h4>
                  <div className="bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg p-3">
                    <div className="text-sm text-[#0A0A0A]/70">
                      <p><strong>{selectedCustomer.points || 0} points</strong> available (= J${((selectedCustomer.points || 0) * (orgSettings?.loyalty_point_value || 1)).toLocaleString()} store credit)</p>
                      <p className="text-xs text-[#0A0A0A]/40 mt-1">Points can be applied as a discount during checkout. Select this customer at the Sales page to use their points.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-sm mb-2">Purchase History</h4>
                  {customerSales.length === 0 ? (
                    <p className="text-sm text-[#0A0A0A]/40">No purchases yet</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {customerSales.map((s, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-[#F5F5F7] rounded text-sm">
                          <div>
                            <div className="font-mono text-xs">{s.receipt_number}</div>
                            <div className="text-xs text-[#0A0A0A]/50">{new Date(s.created_at).toLocaleDateString()}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold">{formatCurrency(s.total_amount)}</div>
                            <div className="text-xs text-[#0A0A0A]/50">{s.items?.length || 0} items</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
